/**
 * Types for the SMS Conversation API.
 */
import { z } from 'zod';
import type { AxiosRequestConfig } from 'axios';
import { createCursorPaginatedResponseSchema } from '@/api/types/pagination.ts';
import { CommunicationType } from '@/constants/CommunicationType.ts';

export enum ConversationKind {
    NATIVE = 'native',
    LEGACY = 'legacy',
}

export const ConversationKindSchema = z.enum(ConversationKind);

const LeadSchema = z.object({
    id: z.number(),
    account_id: z.number(),
    customer_id: z.number().nullable().optional().default(null),
    division_id: z.number().nullable(),
    first_name: z.string(),
    last_name: z.string(),
    locale: z.string(),
    unsubscribe_sms_date: z.string().nullable(),
    unsubscribe_all_date: z.string().nullable(),
    is_casl_expired: z.boolean(),
});

export type ApiLead = z.infer<typeof LeadSchema>;

const LeadPhoneSchema = z.object({
    id: z.number(),
    phone: z.string(),
    type: z.string(),
    preferred: z.boolean().nullable().optional(),
});

export type ApiLeadPhone = z.infer<typeof LeadPhoneSchema>;

const ContactPhoneSchema = z.object({
    id: z.number(),
    phone: z.string(),
    type: z.string().nullable(),
});

export type ApiContactPhone = z.infer<typeof ContactPhoneSchema>;

const ContextLeadSchema = z.object({
    id: z.number(),
    account_id: z.number(),
    customer_id: z.number().nullable(),
    division_id: z.number().nullable(),
    first_name: z.string(),
    last_name: z.string(),
    locale: z.string(),
});

export type ApiContextLead = z.infer<typeof ContextLeadSchema>;

const ContextLeadPhoneSchema = LeadPhoneSchema.extend({
    lead_id: z.number(),
});

export type ApiContextLeadPhone = z.infer<typeof ContextLeadPhoneSchema>;

export const ContactTargetSchema = z.object({
    action: z.literal('sms'),
    lead_id: z.number(),
    contact_id: z.number(),
    channel_kind: z.literal('phone'),
    contact_phone_id: z.number(),
    contact_email_id: z.null(),
    destination: z.string(),
    extension: z.string().nullable(),
    lead_phone_id: z.number().nullable(),
    lead_email_id: z.null(),
});

export type ApiContactTarget = z.infer<typeof ContactTargetSchema>;

const SmsProviderSchema = z.object({
    id: z.number(),
    phone: z.string(),
});

export type ApiSmsProvider = z.infer<typeof SmsProviderSchema>;

const LatestMessageSchema = z.object({
    id: z.number(),
    preview: z.string().nullable(),
    original_created_at: z.iso.datetime({ offset: true }),
});

const ConversationUserSchema = z.object({
    id: z.number(),
    userId: z.number(),
    lastMessageReadId: z.number().nullable(),
    allMessagesReadAt: z.string().nullable(),
});

export type ApiConversationUser = z.infer<typeof ConversationUserSchema>;

export const ConversationSchema = z.object({
    id: z.number(),
    kind: ConversationKindSchema.default(ConversationKind.NATIVE),
    readOnly: z.boolean().default(false),
    archived: z.boolean(),
    original_created_at: z.iso.datetime({ offset: true }),
    lead: LeadSchema,
    leadPhone: LeadPhoneSchema.nullable(),
    contactPhone: ContactPhoneSchema.nullable().default(null),
    smsProvider: SmsProviderSchema.nullable(),
    latestMessage: LatestMessageSchema.nullable(),
    conversationUsers: z.array(ConversationUserSchema),
});

export type ApiConversation = z.infer<typeof ConversationSchema>;

export const SendDisabledReasonValues = [
    'sms_provider_inactive',
    'contact_target_unavailable',
] as const;

export const SendDisabledReasonSchema = z.enum(SendDisabledReasonValues).nullable();

export type SendDisabledReason = z.infer<typeof SendDisabledReasonSchema>;

export const AppointmentStatusSchema = z.object({
    confirmed: z.boolean(),
    canceled: z.boolean(),
    start_at: z.string().nullable(),
});

export type ApiAppointmentStatus = z.infer<typeof AppointmentStatusSchema>;

const MediaSchema = z.object({
    id: z.number(),
    url: z.string(),
    mime_type: z.string(),
    file_name: z.string(),
});

export type ApiMedia = z.infer<typeof MediaSchema>;

export const MessageSchema = z
    .object({
        id: z.number(),
        automation_action_id: z.number().nullable(),
        communication_id: z.number().nullable(),
        communication_type_id: z.enum(CommunicationType),
        conversation_id: z.number().nullable(),
        legacy_sms_conversation_id: z.number().nullable().optional(),
        conversation_kind: ConversationKindSchema.optional(),
        user_id: z.number().nullable(),
        message: z.string().nullable(),
        is_attachment: z.boolean(),
        media: z.array(MediaSchema),
        original_created_at: z.iso.datetime({ offset: true }),
        appointment_id: z.number().nullable(),
        appointment: AppointmentStatusSchema.nullable(),
        status: z.string().nullable(),
        error_code: z.string().nullable(),
    })
    .superRefine((message, context) => {
        if (message.conversation_id !== null || message.legacy_sms_conversation_id != null) {
            return;
        }

        context.addIssue({
            code: 'custom',
            path: ['conversation_id'],
            message: 'A communication message must be linked to a conversation.',
        });
    });

export type MessageItem = z.infer<typeof MessageSchema>;

export const ConversationMessageResponseSchema = z.object({
    data: MessageSchema,
});

export type ConversationMessageResponse = z.infer<typeof ConversationMessageResponseSchema>;

export const NewSmsMessageBroadcastPayloadSchema = z.object({
    conversationId: z.number(),
    messageId: z.number(),
    preview: z.string().nullable(),
    createdAt: z.iso.datetime({ offset: true }),
    userId: z.number().nullable(),
    customerId: z.number().nullable().optional(),
});

export type NewSmsMessageBroadcastPayload = z.infer<typeof NewSmsMessageBroadcastPayloadSchema>;

export const AppointmentStatusChangedPayloadSchema = z.object({
    appointmentId: z.number(),
    confirmed: z.boolean(),
    canceled: z.boolean(),
});

export type AppointmentStatusChangedPayload = z.infer<typeof AppointmentStatusChangedPayloadSchema>;

export const PaginatedConversationListSchema =
    createCursorPaginatedResponseSchema(ConversationSchema);

export type PaginatedConversationList = z.infer<typeof PaginatedConversationListSchema>;

export const PaginatedMessageListWithConversationSchema = createCursorPaginatedResponseSchema(
    MessageSchema,
    {
        conversation: z.object({
            data: ConversationSchema,
        }),
        lastMessageReadId: z.number().nullable(),
        readOnly: z.boolean(),
        canSend: z.boolean().default(true),
        sendDisabledReason: SendDisabledReasonSchema.default(null),
        contextLead: ContextLeadSchema.nullable().optional(),
        contextLeadPhone: ContextLeadPhoneSchema.nullable().optional(),
        contactTarget: ContactTargetSchema.optional(),
    }
);

export type PaginatedMessageListWithConversation = z.infer<
    typeof PaginatedMessageListWithConversationSchema
>;

export const ConversationNotFoundResponseSchema = z.object({
    error: z.object({
        message: z.string(),
        code: z.literal('conversation_not_found'),
    }),
    smsProvider: SmsProviderSchema,
    contactTarget: ContactTargetSchema.optional(),
});

export type ConversationNotFoundResponse = z.infer<typeof ConversationNotFoundResponseSchema>;

export const ToggleArchivedResponseSchema = z.object({
    data: z.object({
        id: z.number(),
        archived: z.boolean(),
    }),
});

export type ToggleArchivedResponse = z.infer<typeof ToggleArchivedResponseSchema>;

export const MediaTemporaryUrlResponseSchema = z.object({
    url: z.url(),
});

export type MediaTemporaryUrlResponse = z.infer<typeof MediaTemporaryUrlResponseSchema>;

export interface FetchConversationsPayload {
    accountId: number;
    perPage: number;
    search?: string;
    intelligentFilter?: boolean;
    archived?: boolean;
    page?: string | null;
}

export interface FetchMessagesPayload {
    perPage: number;
    page?: string | null;
    kind?: ConversationKind;
}

export type FindConversationByLeadPhonePayload =
    | {
          leadId: number;
          leadPhoneId: number;
          contactPhoneId?: never;
          perPage: number;
          page?: string | null;
      }
    | {
          leadId: number;
          contactPhoneId: number;
          leadPhoneId?: never;
          perPage: number;
          page?: string | null;
      };

export interface FetchConversationByCommunicationIdPayload {
    perPage: number;
    page?: string | null;
}

type SmsMessageTargetPayload =
    | {
          leadId: number;
          leadPhoneId: number;
          contactPhoneId?: never;
      }
    | {
          leadId: number;
          contactPhoneId: number;
          leadPhoneId?: never;
      };

export type TextMessagePayload = SmsMessageTargetPayload & {
    conversationId?: number;
    smsProviderId?: number;
    body: string;
    ai_interaction_id?: number;
};

export type AttachmentMessagePayload = SmsMessageTargetPayload & {
    conversationId?: number;
    smsProviderId?: number;
    key: string;
    name: string;
    content_type: string;
};

export const SendMessageResponseSchema = z.object({
    data: MessageSchema,
    conversation: z.object({
        data: ConversationSchema,
    }),
    lastMessageReadId: z.number().nullable().optional(),
    readOnly: z.boolean().optional(),
    canSend: z.boolean().default(true),
    sendDisabledReason: SendDisabledReasonSchema.default(null),
});

export type SendMessageResponse = z.infer<typeof SendMessageResponseSchema>;

export interface ConversationApiInterface {
    fetchConversations(
        payload: FetchConversationsPayload,
        config?: AxiosRequestConfig
    ): Promise<PaginatedConversationList>;
    fetchConversationThread(
        conversationId: number,
        payload: FetchMessagesPayload,
        config?: AxiosRequestConfig
    ): Promise<PaginatedMessageListWithConversation>;
    fetchConversationMessage(
        conversationId: number,
        messageId: number
    ): Promise<ConversationMessageResponse>;
    findConversationByLeadPhone(
        payload: FindConversationByLeadPhonePayload
    ): Promise<PaginatedMessageListWithConversation | ConversationNotFoundResponse | null>;
    fetchConversationByCommunicationId(
        communicationId: number,
        payload: FetchConversationByCommunicationIdPayload
    ): Promise<PaginatedMessageListWithConversation>;
    sendMessage(
        payload: TextMessagePayload | AttachmentMessagePayload
    ): Promise<SendMessageResponse>;
    toggleArchived(conversationId: number): Promise<ToggleArchivedResponse>;
    retryMessage(conversationId: number, messageId: number): Promise<ConversationMessageResponse>;
    fetchMediaTemporaryUrl(mediaId: number): Promise<MediaTemporaryUrlResponse>;
}
